import { ReactNode, forwardRef, useEffect, useMemo, useRef, isValidElement } from 'react';
import { GripVertical, X, Maximize2, Pencil, Expand, Link2, Link2Off, EyeOff, Lock, Clock3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface DashboardWidgetProps {
  title: string;
  children: ReactNode;
  icon?: ReactNode;
  onRemove?: () => void;
  onEdit?: () => void;
  onMaximize?: () => void;
  className?: string;
  dragHandleClass?: string;
  isEditing?: boolean;
  isMinimized?: boolean;
  isMaximized?: boolean;
  syncMode?: 'linked' | 'isolated' | 'fixed';
  fixedTimestamp?: string;
  showFixedBadge?: boolean;
  onToggleSync?: () => void;
  onToggleMinimize?: (minimized: boolean) => void;
  onHide?: () => void;
  extraActions?: ReactNode;
}

type IconBtnProps = {
  onClick: (e: React.MouseEvent) => void;
  title?: string;
  ariaLabel?: string;
  children: ReactNode;
  className?: string;
};

// Reusable icon button (must forwardRef because Radix/drag libraries attach refs)
const IconBtn = forwardRef<HTMLButtonElement, IconBtnProps>(
  ({ onClick, title, ariaLabel, children, className }, ref) => (
    <Button
      ref={ref}
      type="button"
      variant="ghost"
      size="icon"
      className={cn("h-6 w-6 widget-action", className)}
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
    >
      {children}
    </Button>
  )
);
IconBtn.displayName = 'IconBtn';

export function DashboardWidget({
  title,
  children,
  icon,
  onRemove,
  onEdit,
  onMaximize,
  className,
  dragHandleClass = 'widget-drag-handle',
  isEditing = false,
  isMinimized = false,
  syncMode = 'linked',
  fixedTimestamp,
  showFixedBadge = false,
  onToggleSync,
  onToggleMinimize,
  onHide,
  extraActions
}: DashboardWidgetProps) {
  const isSyncLinked = syncMode === 'linked';
  const fixedTimeLabel = useMemo(() => {
    if (syncMode !== 'fixed' || !fixedTimestamp) return null;
    const parsed = new Date(fixedTimestamp);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, [fixedTimestamp, syncMode]);
  // Edit / Remove / Maximize handlers — defensive wrappers so components don't throw if props are missing
  const handleEdit = (e?: React.MouseEvent) => {
    e?.stopPropagation?.();
    if (onEdit) onEdit();
  };

  const handleRemove = (e?: React.MouseEvent) => {
    e?.stopPropagation?.();
    if (onRemove) onRemove();
  };

  const handleMaximize = (e?: React.MouseEvent) => {
    e?.stopPropagation?.();
    if (onMaximize) onMaximize();
  };

  const handleHide = (e?: React.MouseEvent) => {
    e?.stopPropagation?.();
    if (onHide) onHide();
  };

  const handleToggleSync = (e?: React.MouseEvent) => {
    e?.stopPropagation?.();
    if (onToggleSync) onToggleSync();
  };

  const handleExpand = (e?: React.MouseEvent) => {
    e?.stopPropagation?.();
    if (onToggleMinimize) onToggleMinimize(false);
  };

  // Auto-unlock minimized widgets after 3 seconds of hover with no widget movement
  const widgetRef = useRef<HTMLDivElement>(null);
  const autoUnlockTimerRef = useRef<NodeJS.Timeout | null>(null);
  const widgetPosRef = useRef<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!isMinimized || isEditing || !widgetRef.current || !onToggleMinimize) return;

    const element = widgetRef.current;

    const clearTimer = () => {
      if (autoUnlockTimerRef.current) {
        clearTimeout(autoUnlockTimerRef.current);
        autoUnlockTimerRef.current = null;
      }
    };

    const startTimer = () => {
      clearTimer();
      autoUnlockTimerRef.current = setTimeout(() => {
        if (onToggleMinimize) onToggleMinimize(false);
      }, 3000);
    };

    const checkPosition = () => {
      const rect = element.getBoundingClientRect();
      const currentPos = { top: rect.top, left: rect.left };
      
      if (widgetPosRef.current) {
        const moved = 
          Math.abs(currentPos.top - widgetPosRef.current.top) > 2 ||
          Math.abs(currentPos.left - widgetPosRef.current.left) > 2;
        
        if (moved) {
          clearTimer();
          widgetPosRef.current = currentPos;
        }
      } else {
        widgetPosRef.current = currentPos;
      }
    };

    const handleMouseEnter = () => {
      widgetPosRef.current = element.getBoundingClientRect();
      startTimer();
    };

    const handleMouseLeave = () => {
      clearTimer();
      widgetPosRef.current = null;
    };

    // Check for position changes while hovering
    const positionCheckInterval = setInterval(() => {
      if (autoUnlockTimerRef.current) {
        checkPosition();
      }
    }, 100);

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      clearTimer();
      clearInterval(positionCheckInterval);
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isMinimized, isEditing, onToggleMinimize]);

  // Minimized compact tile view
  if (isMinimized) {
    return (
      <div
        ref={widgetRef}
        className={cn(
          "h-full w-full flex items-center justify-center bg-transparent overflow-hidden relative group cursor-pointer transition-all duration-200",
          isEditing && dragHandleClass,
          className
        )}
        onClick={() => !isEditing && handleExpand()}
      >
        <div className="flex flex-col items-center justify-center gap-1 p-0">
          <div className="relative">
            {icon ? (
              <div className="text-primary w-6 h-6 flex items-center justify-center">{icon}</div>
            ) : (
              <div className="w-6 h-6 flex items-center justify-center">
                <span className="text-xs font-bold text-primary">{title.charAt(0)}</span>
              </div>
            )}
          </div>
          <span className="text-[10px] font-medium text-muted-foreground truncate max-w-full px-1 text-center leading-tight">
            {title.length > 12 ? title.slice(0, 10) + '\u2026' : title}
          </span>
        </div>

        {isEditing && (
          <>
            <div className="absolute top-0.5 left-0.5 flex items-center gap-0.5">
              {onToggleSync && (
                <IconBtn
                  onClick={handleToggleSync}
                  ariaLabel={isSyncLinked ? 'Timeline linked' : 'Timeline disconnected'}
                  className={cn(
                    "h-5 w-5 opacity-60 hover:opacity-100 bg-background/40",
                    isSyncLinked ? "text-emerald-500" : "text-rose-500"
                  )}
                >
                  {isSyncLinked ? <Link2 className="w-2.5 h-2.5" /> : <Link2Off className="w-2.5 h-2.5" />}
                </IconBtn>
              )}
              {onEdit && (
                <IconBtn onClick={handleEdit} title="Edit widget" className="h-5 w-5 opacity-60 hover:opacity-100 bg-background/40">
                  <Pencil className="w-2.5 h-2.5" />
                </IconBtn>
              )}
              <IconBtn onClick={handleExpand} title="Expand" className="h-5 w-5 opacity-60 hover:opacity-100 bg-background/40">
                <Maximize2 className="w-3 h-3" />
              </IconBtn>
              {onRemove && (
                <IconBtn onClick={handleRemove} title="Remove" className="h-5 w-5 opacity-60 hover:opacity-100 bg-background/40">
                  <X className="w-2.5 h-2.5" />
                </IconBtn>
              )}
            </div>
            <div className="absolute bottom-0.5 left-0.5">
              <GripVertical className="w-3 h-3 text-muted-foreground/40" />
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={cn(
      "h-full flex flex-col bg-card/40 backdrop-blur-sm rounded-xl border border-border/50 overflow-hidden relative group transition-all duration-300",
      className
    )}>

      {showFixedBadge && syncMode === 'fixed' && fixedTimeLabel ? (
        <div className="pointer-events-none absolute left-2 top-2 z-20 inline-flex items-center gap-1 rounded-md border border-amber-400/30 bg-amber-500/15 px-2 py-1 text-[10px] font-medium text-amber-100 shadow-sm backdrop-blur-sm">
          <Lock className="h-2.5 w-2.5" />
          <Clock3 className="h-2.5 w-2.5" />
          <span>{fixedTimeLabel}</span>
        </div>
      ) : null}

      {isEditing && (
        <div className="pointer-events-none absolute inset-x-2 top-2 z-20 flex items-start justify-between gap-2 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150">
          <div
            className={cn(
              "pointer-events-auto inline-flex items-center gap-2 rounded-md border border-border/50 bg-card/80 px-2 py-1 shadow-sm backdrop-blur-sm",
              dragHandleClass,
            )}
          >
            <GripVertical className="w-4 h-4 text-muted-foreground/60" />
            {icon && <div className="text-primary/70">{icon}</div>}
            <h3 className="text-xs font-medium text-foreground/85 truncate max-w-[180px]">{title}</h3>
          </div>

          <div className="pointer-events-auto inline-flex items-center gap-1 rounded-md border border-border/50 bg-card/80 px-1 py-1 shadow-sm backdrop-blur-sm">
            {extraActions}
            {onToggleSync && (
              <IconBtn
                onClick={handleToggleSync}
                ariaLabel={isSyncLinked ? 'Timeline linked' : 'Timeline disconnected'}
                className={cn("opacity-80 hover:opacity-100", isSyncLinked ? "text-emerald-500" : "text-rose-500")}
              >
                {isSyncLinked ? <Link2 className="w-3 h-3" /> : <Link2Off className="w-3 h-3" />}
              </IconBtn>
            )}
            {onEdit && (
              <IconBtn onClick={handleEdit} title="Edit widget" className="opacity-80 hover:opacity-100">
                <Pencil className="w-3 h-3" />
              </IconBtn>
            )}
            {onMaximize && (
              <IconBtn onClick={handleMaximize} title="Maximize widget" className="opacity-80 hover:opacity-100">
                <Expand className="w-3 h-3" />
              </IconBtn>
            )}
            {onHide && (
              <IconBtn onClick={handleHide} title="Hide widget" className="opacity-80 hover:opacity-100">
                <EyeOff className="w-3 h-3" />
              </IconBtn>
            )}
            {onRemove && (
              <IconBtn onClick={handleRemove} title="Remove" className="opacity-80 hover:opacity-100">
                <X className="w-3 h-3" />
              </IconBtn>
            )}
          </div>
        </div>
      )}

      {!isEditing && (onHide || onMaximize) && (
        <div className="pointer-events-none absolute right-2 top-2 z-20 inline-flex items-center gap-1 rounded-md border border-border/50 bg-card/80 px-1 py-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150 shadow-sm backdrop-blur-sm">
          {onMaximize && (
            <div className="pointer-events-auto">
              <IconBtn onClick={handleMaximize} title="Maximize widget" className="opacity-80 hover:opacity-100">
                <Expand className="w-3 h-3" />
              </IconBtn>
            </div>
          )}
          {onHide && (
            <div className="pointer-events-auto">
              <IconBtn onClick={handleHide} title="Hide widget" className="opacity-80 hover:opacity-100">
                <EyeOff className="w-3 h-3" />
              </IconBtn>
            </div>
          )}
        </div>
      )}

      <div className={cn("flex-1 overflow-auto p-2 md:p-3")}>{
        isValidElement(children) || typeof children === 'string' || typeof children === 'number'
          ? children
          : (
            <div className="text-sm text-muted-foreground">No widget content</div>
          )
      }</div>

      {isEditing && (
        <div className="absolute bottom-0 right-0 w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <svg className="w-4 h-4 text-muted-foreground/50" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM22 14H20V12H22V14ZM18 18H16V16H18V18ZM14 22H12V20H14V22Z" />
          </svg>
        </div>
      )}
    </div>
  );
}
